import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { ImageScaleView, animate } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import {
  animDuration,
  getScreenDimensions,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import {
  ChestType,
  getMatch2ChestOnBeforeClaim,
} from '../rewards/match2-rewards.utils';
import sounds from 'src/lib/sounds';
import { RewardCard } from './RewardCard';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { createEmitter } from 'src/lib/Emitter';

type CloseHandler = (claimed: boolean) => void;

export default class PopupMatch2Chest extends PopupBasicLazy {
  private stage: ImageScaleView;
  private chest: ImageScaleView;
  private shadow: ImageScaleView;
  private chestType: ChestType = 'green';
  private rays: ImageScaleView;
  private button: ButtonScaleViewWithText;
  private rewards!: Match2Reward[];
  private rewardItems: RewardCard[] = [];
  private titleBanner: ImageScaleView;
  private cardsContainer: FlexBoxView;
  private onBeforeClaim?: () => Promise<boolean>;
  private closeHandler: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.MATCH2.DAILY_BONUS._,
        $subFeature: Feature.MATCH2.DAILY_BONUS.COLLECT,
      },
      zIndex: 10000,
    });
    this.closeHandler = opts.close;
    this.box.removeFromSuperview();

    this.stage = new ImageScaleView({
      superview: this.root,
      image: 'assets/ui/match2/chest/stage.png',
      width: opts.superview.style.width,
      height: getScreenDimensions().height,
      x: this.root.style.width * 0.5,
      y: this.root.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'cover',
    });

    const chestPos = this.stage.style.height * 0.65;

    this.shadow = new ImageScaleView({
      superview: this.stage,
      image: 'assets/ui/match2/chest/shadow.png',
      width: 442,
      height: 105,
      x: this.stage.style.width * 0.5,
      y: chestPos + 166,
      centerOnOrigin: true,
    });

    this.rays = new ImageScaleView({
      superview: this.stage,
      image: 'assets/ui/match2/chest/ray.png',
      width: 529,
      height: 522,
      x: this.stage.style.width * 0.5,
      y: chestPos + 20,
      centerOnOrigin: true,
    });

    this.chest = new ImageScaleView({
      superview: this.stage,
      width: 510,
      height: 377,
      x: this.stage.style.width * 0.5,
      y: chestPos,
      centerOnOrigin: true,
    });

    this.button = new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: this.stage,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 235,
      height: 90,
      x: this.stage.style.width * 0.5,
      y: chestPos + 300,
      localeText: () => i18n('match2.chests.open'),
      onClick: this.openChest.bind(this),
      tracking: {
        feature: this.opts.tracking.feature,
        $subFeature: `${this.opts.tracking.$subFeature}-match2-cta`,
      },
      centerOnOrigin: true,
    });

    this.titleBanner = new ImageScaleView({
      superview: this.stage,
      image: `assets/ui/match2/common/banner_red.png`,
      width: 390,
      height: 107,
      x: this.stage.style.width * 0.5,
      y: this.stage.style.height * 0.2 - 100,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleX: 0,
    });

    new LangBitmapFontTextShadowView({
      superview: this.titleBanner,
      localeText: () => i18n('match2.chests.title'),
      width: this.titleBanner.style.width - 80,
      height: this.titleBanner.style.height,
      align: 'center',
      verticalAlign: 'center',
      size: 46,
      color: '#FFFFFF',
      font: 'Body',
      isRichText: true,
      canHandleEvents: false,
      centerOnOrigin: true,
      x: this.titleBanner.style.width * 0.5,
      y: this.titleBanner.style.height * 0.5 - 10,
      textShadowOffsetX: 0,
      textShadowOffsetY: 5,
      textShadowColor: '#9B2129',
    });

    this.cardsContainer = new FlexBoxView({
      superview: this.stage,
      width: this.stage.style.width,
      height: 500,
      y: this.stage.style.height * 0.18,
      flex: {
        justifyContent: 'space-evenly',
        flexWrap: 'wrap',
      },
    });

    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.stage.updateOpts({
          height: screen.height,
          y: this.root.style.height * 0.5,
          centerOnOrigin: true,
        });
      },
    );
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup(AssetGroup.constructURLs(['assets/ui/match2/chest']));
  }

  public async init(opts: { rewards: Match2Reward[]; chestType: ChestType }) {
    super.init(opts);
    this.chest.updateOpts({
      image: `assets/ui/match2/chest/${opts.chestType}_closed.png`,
      anchorY: 377,
    });
    this.chestType = opts.chestType;
    this.rewards = opts.rewards;
    // Get callback from module scope (not passed through Redux)
    this.onBeforeClaim = getMatch2ChestOnBeforeClaim();

    // Create reward views.
    this.rewardItems = this.rewards.map((reward) => {
      const item = new RewardCard({ reward });
      getFlexNode(item).updateStyle({
        marginVertical: 10,
      });
      item.updateOpts({
        superview: this.cardsContainer,
        opacity: 0,
      });
      return item;
    });
  }

  public override async fadeIn() {
    // Set initial state.
    this.stage.updateOpts({ opacity: 0 });
    const chestFinalPositon = this.chest.style.y;
    this.chest.updateOpts({ y: -this.chest.style.height });
    this.shadow.updateOpts({ scale: 0 });
    this.rays.updateOpts({ scale: 0 });
    this.button.updateOpts({ opacity: 0, visible: false });

    // Load sounds.
    await withLoading(
      () =>
        new AssetGroup([
          'assets/sounds/sfx/Ascension.mp3',
          `assets/sounds/sfx/GachaItemDrop.mp3`,
          `assets/sounds/sfx/NormalHit.mp3`,
          `assets/sounds/sfx/Move.mp3`,
        ]).load(),
      {
        feature: Feature.CHEST._,
        subFeature: Feature.CHEST.ASSETS,
      },
    );

    sounds.sfx.play('ascension');

    // Fade in overlay.
    await super.fadeIn();

    // Fade in stage
    this.stage.updateOpts({ visible: true });
    await animate(this.stage)
      .then({ opacity: 1 }, animDuration, animate.easeOut)
      .toPromise();

    // Define the speed of the chest falling animation.
    const chestFallDuration = 500;

    // Rays appear.
    const rayAppearDuration = chestFallDuration + animDuration;
    const loopDuration = 25 * animDuration;
    const rotateRays = () =>
      animate(this.rays)
        .now({ r: 0 })
        .then(
          { scale: 1, r: (Math.PI * rayAppearDuration) / loopDuration },
          rayAppearDuration,
          animate.linear,
        )
        .then({ r: Math.PI }, loopDuration - rayAppearDuration, animate.linear)
        .then({ r: Math.PI * 2 }, loopDuration, animate.linear)
        .then(rotateRays);

    rotateRays();

    // Chest falls from the sky.
    animate(this.shadow).then(
      { scale: 1 },
      chestFallDuration,
      animate.easeOutQuad,
    );
    await animate(this.chest)
      .then({ y: chestFinalPositon }, chestFallDuration, animate.easeInOutQuad)
      .toPromise();
    sounds.sfx.play('normalHit');

    // Chest bounces.
    await animate(this.chest)
      .then(
        {
          y: chestFinalPositon - 20,
          r: Math.PI / 16,
        },
        100,
        animate.easeOutQuad,
      )
      .then(
        {
          y: chestFinalPositon,
          r: 0,
        },
        100,
        animate.easeInQuad,
      )
      .toPromise();

    // Auto open chest after 0.3 second
    await waitForItPromise(300);
    this.openChest();
  }

  public override async fadeOut() {
    super.fadeOut();
    await animate(this.stage).then({ opacity: 0 }, animDuration).toPromise();
  }

  private async openChest() {
    // Fade out button.
    animate(this.button).then({ opacity: 0 }, animDuration, animate.easeOut);

    // Hide rays.
    animate(this.rays).now({ scale: 0 }, animDuration, animate.easeOut);

    // Chest opens.
    await animate(this.chest)
      .then(
        {
          scaleX: 1.2,
          scaleY: 0.8,
        },
        200,
        animate.easeOutQuad,
      )
      .then(
        {
          scaleX: 0.8,
          scaleY: 1.2,
        },
        200,
        animate.easeInQuad,
      )
      .then(() => {
        // Swap chest image.
        this.chest.updateOpts({
          image: `assets/ui/match2/chest/${this.chestType}_open.png`,
        });
      })
      .toPromise();

    // Reset chest scale.
    animate(this.chest).then(
      {
        scaleX: 1,
        scaleY: 1,
      },
      500,
      animate.easeOutQuart,
    );

    // Show title banner.
    animate(this.titleBanner).then(
      { scaleX: 1 },
      animDuration,
      animate.easeOut,
    );

    // Store final positions for rewards. It's calculated by the flexbox container.
    const finalPositions = this.rewardItems.map((reward) => {
      return {
        x: reward.style.x,
        y: reward.style.y + this.cardsContainer.style.y,
        scale: 1,
        opacity: 1,
      };
    });

    // Set initial positions for animation. And reparent rewards to the stage.
    this.rewardItems.forEach((reward) => {
      reward.updateOpts({
        superview: this.stage,
        x: this.chest.style.x - reward.style.width * 0.5,
        y: this.chest.style.y,
        scale: 0.5,
        opacity: 0,
      });
    });

    // Move rewards to their final positions.
    await Promise.all(
      this.rewardItems.map((reward, i) => {
        sounds.sfx.play('move');
        return animate(reward)
          .wait(i * animDuration)
          .then(finalPositions[i], 500, animate.easeOutQuart)
          .toPromise();
      }),
    );

    // Turn each reward with a delay between them.
    await Promise.all(
      this.rewardItems.map(async (reward, i) => {
        await animate(reward)
          .wait(i * animDuration)
          .then(reward.turn)
          .toPromise();
        sounds.sfx.play('gachaItemDrop');
      }),
    );

    // Reparent rewards to the cards container jusxt in case.
    this.rewardItems.forEach((reward) => {
      reward.updateOpts({
        superview: this.cardsContainer,
      });
    });

    // Switch button to claim.
    this.button.updateOpts({ visible: true });
    this.button.localeText = () => i18n('match2.chests.claim');
    this.button.onClick = async () => {
      sounds.sfx.play('ok');
      if (this.onBeforeClaim) {
        const shouldProceed = await this.onBeforeClaim();
        if (!shouldProceed) {
          this.closeHandler(false);
          return;
        }
      }
      this.closeHandler(true);
    };

    // Fade in button.
    await animate(this.button).then(
      { opacity: 1 },
      animDuration,
      animate.easeOut,
    ).toPromise;
  }
}
